/**
 * User API Service
 * 
 * Endpoints:
 * GET    /users/me          - Eigene Profildaten
 * PATCH  /users/me          - Profil aktualisieren (partial)
 * DELETE /users/me          - Account löschen
 * GET    /users/{user_id}   - Profil anderer User (privacy-aware)
 * 
 * Settings:
 * GET    /users/me/settings - Settings laden
 * PUT    /users/me/settings - Settings speichern (vollständig)
 * 
 * Privacy:
 * GET    /users/me/privacy  - Privacy-Einstellungen laden
 * PUT    /users/me/privacy  - Privacy-Einstellungen speichern (vollständig)
 */

import { get, patch, put, del } from '../client';

// ============================================================================
// PROFILE
// ============================================================================

/**
 * Eigene Profildaten laden
 * @returns {Promise<{success: boolean, data?: UserProfile, error?: object}>}
 * 
 * Response Data:
 * {
 *   id: number,
 *   username: string,
 *   email: string,
 *   bio: string,
 *   role: number,
 *   favorite_genres: string[],
 *   social_links: string[],
 *   avatar_has: boolean,
 *   avatar_media_key: string
 * }
 */
export const getProfile = async () => {
  return get('/users/me');
};

/**
 * Profil aktualisieren (Partial Update)
 * @param {object} updates - { username?, bio?, favorite_genres?, social_links? }
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 409 USERNAME_EXISTS
 */
export const updateProfile = async (updates) => {
  // Konvertiere camelCase zu snake_case falls nötig
  const payload = {};
  if (updates.username !== undefined) payload.username = updates.username;
  if (updates.bio !== undefined) payload.bio = updates.bio;
  if (updates.favorite_genres !== undefined) payload.favorite_genres = updates.favorite_genres;
  if (updates.favoriteGenres !== undefined) payload.favorite_genres = updates.favoriteGenres;
  if (updates.social_links !== undefined) payload.social_links = updates.social_links;
  if (updates.socialLinks !== undefined) payload.social_links = updates.socialLinks;

  return patch('/users/me', payload);
};

/**
 * Account löschen
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 */
export const deleteAccount = async () => {
  return del('/users/me');
};

/**
 * Vollständiger Export aller persönlichen Daten (§7): Profil, Settings,
 * Privacy, Library, Wishlist, Benachrichtigungs-Overrides, Notifications,
 * Freundesliste.
 * @returns {Promise<{success: boolean, data?: object, error?: object}>}
 */
export const exportData = async () => {
  return get('/users/me/export');
};

/**
 * Passwort ändern
 * @param {string} currentPassword - Aktuelles Passwort
 * @param {string} newPassword     - Neues Passwort (min. 8 Zeichen, Groß/Klein/Zahl/Sonderzeichen)
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 400 VALIDATION_ERROR, 401 INVALID_PASSWORD
 */
export const changePassword = async (currentPassword, newPassword) => {
  return patch('/users/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
};

/**
 * Profil eines anderen Users laden (privacy-aware)
 * @param {number} userId - User ID
 * @returns {Promise<{success: boolean, data?: UserProfile, error?: object}>}
 * 
 * Errors: 403 PRIVATE_RESOURCE, 404 NOT_FOUND
 */
export const getUserById = async (userId) => {
  return get(`/users/${userId}`);
};

/**
 * User per Benutzername suchen
 * @param {string} username - Benutzername (mind. 2 Zeichen)
 * @returns {Promise<{success: boolean, data?: {items: UserSearchResult[]}, error?: object}>}
 * 
 * Response Item: { id: number, username: string, avatar_media_key: string }
 * 
 * Requires backend: GET /users/search?q=username
 */
export const searchUsers = async (username) => {
  return get('/users/search', { q: username.trim() });
};

// ============================================================================
// SETTINGS
// ============================================================================

/**
 * Settings laden
 * @returns {Promise<{success: boolean, data?: UserSettings, error?: object}>}
 * 
 * Response Data:
 * {
 *   newsletter: boolean,
 *   email_notifications: boolean,
 *   push_notifications: boolean,
 *   new_release_alerts: boolean,
 *   wishlist_alerts: boolean,
 *   language: string,
 *   theme: string,
 *   profile_public: boolean,
 *   discover_personalized: boolean
 * }
 */
export const getSettings = async () => {
  return get('/users/me/settings');
};

/**
 * Settings speichern (vollständig, kein Partial!)
 * @param {object} settings - Vollständiges Settings-Objekt
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 400 VALIDATION_ERROR
 */
export const updateSettings = async (settings) => {
  // Konvertiere zu snake_case
  const payload = {
    newsletter: settings.newsletter,
    email_notifications: settings.email_notifications ?? settings.emailNotifications,
    push_notifications: settings.push_notifications ?? settings.pushNotifications,
    new_release_alerts: settings.new_release_alerts ?? settings.newReleaseAlerts,
    wishlist_alerts: settings.wishlist_alerts ?? settings.wishlistAlerts,
    language: settings.language,
    theme: settings.theme,
    profile_public: settings.profile_public ?? settings.profilePublic,
    // §30: Opt-out aus der Genre-/Sammlungs-basierten Discover-Personalisierung
    discover_personalized: settings.discover_personalized ?? settings.discoverPersonalized ?? true,
  };

  return put('/users/me/settings', payload);
};

// ============================================================================
// PRIVACY
// ============================================================================

/**
 * Privacy-Einstellungen laden
 * @returns {Promise<{success: boolean, data?: PrivacySettings, error?: object}>}
 * 
 * Response Data:
 * {
 *   show_library: boolean,
 *   show_wishlist: boolean,
 *   show_stats: boolean,
 *   show_profile: boolean,
 *   show_manga_count: boolean,
 *   show_band_count: boolean,
 *   show_favorite_manga: boolean,
 *   show_read_manga: boolean
 * }
 */
export const getPrivacy = async () => {
  return get('/users/me/privacy');
};

/**
 * Privacy-Einstellungen speichern (vollständig, kein Partial!)
 * @param {object} privacy - Vollständiges Privacy-Objekt (8 Felder, §32)
 * @returns {Promise<{success: boolean, data?: {message: string}, error?: object}>}
 *
 * Errors: 400 VALIDATION_ERROR
 */
export const updatePrivacy = async (privacy) => {
  // Konvertiere zu snake_case
  const payload = {
    show_library: privacy.show_library ?? privacy.showLibrary,
    show_wishlist: privacy.show_wishlist ?? privacy.showWishlist,
    show_stats: privacy.show_stats ?? privacy.showStats,
    show_profile: privacy.show_profile ?? privacy.showProfile,
    // §32: granulare Freundesprofil-Felder statt eines einzigen show_stats-Schalters
    show_manga_count: privacy.show_manga_count ?? privacy.showMangaCount ?? true,
    show_band_count: privacy.show_band_count ?? privacy.showBandCount ?? true,
    show_favorite_manga: privacy.show_favorite_manga ?? privacy.showFavoriteManga ?? true,
    show_read_manga: privacy.show_read_manga ?? privacy.showReadManga ?? true,
  };

  return put('/users/me/privacy', payload);
};

// ============================================================================
// TYPES (JSDoc)
// ============================================================================

/**
 * @typedef {object} UserProfile
 * @property {number} id
 * @property {string} username
 * @property {string} email
 * @property {string} bio
 * @property {number} role
 * @property {string[]} favorite_genres
 * @property {string[]} social_links
 * @property {boolean} avatar_has
 * @property {string} avatar_media_key
 */

/**
 * @typedef {object} UserSettings
 * @property {boolean} newsletter
 * @property {boolean} email_notifications
 * @property {boolean} push_notifications
 * @property {boolean} new_release_alerts
 * @property {boolean} wishlist_alerts
 * @property {string} language
 * @property {string} theme
 * @property {boolean} profile_public
 */

/**
 * @typedef {object} PrivacySettings
 * @property {boolean} show_library
 * @property {boolean} show_wishlist
 * @property {boolean} show_stats
 * @property {boolean} show_profile
 */
